import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Crown, ArrowRight, Check } from "lucide-react";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

const PERKS = [
  "Tier Platino istantaneo (salta 50 inviti)",
  "Priorità ingresso su ogni nuova apertura",
  "Upgrade suite garantito ove disponibile",
  "Concierge dedicato via WhatsApp",
  "Invito alle serate private di inaugurazione",
  "Menzione permanente come Founding Member",
];

export function FoundingMember({ user }) {
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState({ count: 0, remaining: 100, max: 100, current_price: 9, next_price: 19, slots_before_price_bump: 80 });

  useEffect(() => {
    api.get("/founding/count").then((r) => setSlots(r.data)).catch(() => {});
  }, [user?.is_founding_member]);

  if (user?.is_founding_member) {
    return (
      <section
        data-testid="founding-badge-section"
        className="bg-white border-t border-b border-[#E5E5E5] py-16 px-6 md:px-12"
      >
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <div className="sc-overline inline-flex items-center gap-2 text-[#0B192C]">
            <Crown size={13} /> Sei Founding Member
          </div>
          <h3 className="font-display font-light text-2xl md:text-3xl" style={{ letterSpacing: "-0.02em" }}>
            Il tier Platino è tuo. Grazie per credere nel club dal primo giorno.
          </h3>
          <Link
            to={`/certificato/${user.referral_code}`}
            data-testid="view-certificate-btn"
            className="sc-btn-primary inline-flex items-center gap-2"
          >
            <Crown size={14} /> Vedi il tuo certificato
          </Link>
        </div>
      </section>
    );
  }

  const soldOut = slots.remaining <= 0;
  const scarcity = slots.remaining <= slots.max * 0.3;

  const start = async () => {
    if (!user) {
      toast.error("Iscriviti prima alla waitlist.");
      return;
    }
    setLoading(true);
    track("founding_checkout_start", { price: slots.current_price });
    try {
      const { data } = await api.post("/payments/founding-checkout", {
        referral_code: user.referral_code,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile avviare il checkout");
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="founding-section"
      className="bg-[#F7F7F8] py-24 md:py-32 px-6 md:px-12 border-t border-[#E5E5E5]"
    >
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
        <div>
          <div className="sc-overline mb-6 inline-flex items-center gap-2">
            <Crown size={13} /> Founding Member · Opzione premium
          </div>
          <h2 className="font-display font-light text-4xl md:text-5xl mb-6" style={{ letterSpacing: "-0.02em" }}>
            Salta la coda. <span className="italic font-extralight">Sblocca il Platino.</span>
          </h2>
          <p className="text-neutral-600 leading-relaxed mb-6">
            Con una quota una tantum di <strong className="text-[#0B192C]">{slots.current_price.toFixed(2).replace(".", ",")} €</strong> ottieni
            l'accesso al tier massimo del club senza dover invitare 50 amici. Il tuo nome resta
            per sempre tra i primi che hanno reso possibile SuiteCrowd.
          </p>
          <div className="text-[11px] text-neutral-500 tracking-wider">
            Pagamento gestito da Stripe · Fondi in escrow · Rimborsabili se il club non parte entro 12 mesi
          </div>
        </div>
        <div className="bg-white border border-[#E5E5E5] p-8 md:p-10 space-y-6">
          <div className="flex justify-between items-start gap-4">
            <div className="flex items-baseline gap-2">
              <div className="font-display font-light text-5xl text-[#0B192C]">
                {slots.current_price.toFixed(2).replace(".", ",")} €
              </div>
              <div className="text-sm text-neutral-500">una tantum</div>
            </div>
            <div className="text-right">
              <div
                data-testid="scarcity-remaining"
                className={`font-display text-2xl font-light ${scarcity ? "text-[#B45309]" : "text-[#0B192C]"}`}
              >
                {slots.remaining}
              </div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 mt-1">
                slot rimasti
              </div>
            </div>
          </div>
          {slots.next_price && slots.slots_before_price_bump > 0 && (
            <div
              data-testid="price-bump-warning"
              className="text-[11px] tracking-wider text-[#B45309] bg-amber-50 border border-amber-100 px-3 py-2 -mt-2"
            >
              Ancora {slots.slots_before_price_bump} slot a {slots.current_price.toFixed(0)}€ ·
              poi il prezzo sale a {slots.next_price.toFixed(0)}€
            </div>
          )}
          {!slots.next_price && slots.remaining > 0 && (
            <div className="text-[11px] tracking-wider text-[#B45309] bg-amber-50 border border-amber-100 px-3 py-2 -mt-2">
              Ultimi {slots.remaining} slot al prezzo massimo di {slots.current_price.toFixed(0)}€
            </div>
          )}
          <div>
            <div className="h-[3px] w-full bg-[#F0F0F0] overflow-hidden">
              <div
                className={`h-full transition-all duration-700 ${scarcity ? "bg-[#B45309]" : "bg-[#0B192C]"}`}
                style={{ width: `${Math.min(100, (slots.count / slots.max) * 100)}%` }}
              />
            </div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500 mt-2">
              {slots.count} di {slots.max} già assegnati
            </div>
          </div>
          <ul className="space-y-3 text-[14px] text-neutral-700 pt-2">
            {PERKS.map((p, i) => (
              <li key={i} className="flex items-start gap-2">
                <Check size={14} className="mt-1 text-[#0B192C] flex-shrink-0" strokeWidth={1.5} />
                <span>{p}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={start}
            disabled={loading || !user || soldOut}
            data-testid="founding-cta"
            className="sc-btn-primary w-full inline-flex items-center justify-center gap-2"
          >
            {soldOut
              ? "Tutti gli slot sono stati assegnati"
              : loading
              ? "Reindirizzamento..."
              : user
              ? "Diventa Founding Member"
              : "Iscriviti prima alla waitlist"}
            {user && !loading && !soldOut && <ArrowRight size={14} />}
          </button>
          {scarcity && !soldOut && (
            <p className="text-[11px] text-[#B45309] text-center tracking-wider" data-testid="scarcity-warning">
              Ultimi slot disponibili
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
